import { useState, useEffect } from "react";
import { CloseIcon } from "src/assets/icons";
import {
  AlertContainer,
  AlertContent,
  AlertFrame,
  CloseButton,
  Description,
  Footer,
  IconCircle,
  TextContent,
  Title,
} from "./AlertBanner.styles";

interface AlertBannerProps {
  title: string;
  description?: string;
  descriptionHTML?: string;
  footer?: string;
  onClose?: () => void;
  storageKey?: string;
}

const DEFAULT_STORAGE_KEY = "login_alert_banner_dismissed";

export const AlertBanner = ({
  title,
  description,
  descriptionHTML,
  footer,
  onClose,
  storageKey = DEFAULT_STORAGE_KEY,
}: AlertBannerProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    // Check localStorage to see if alert has been dismissed
    const dismissed = localStorage.getItem(storageKey);
    if (!dismissed) {
      // Only render if not dismissed
      setShouldRender(true);
      setIsVisible(true);
    }
  }, [storageKey]);

  const handleClose = () => {
    setIsVisible(false);
    // Store dismissal in localStorage
    localStorage.setItem(storageKey, "true");
    // Remove from DOM after animation completes
    setTimeout(() => {
      setShouldRender(false);
      if (onClose) {
        onClose();
      }
    }, 300);
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <AlertContainer isVisible={isVisible}>
      <AlertFrame>
        <AlertContent>
          <IconCircle>
            <svg
              width="18"
              height="17"
              viewBox="0 0 18 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M7.26111 0.211173C8.10935 -0.0703909 9.02347 -0.0703909 9.87171 0.211173C10.7164 0.491571 11.3797 1.15659 12.046 2.09096C12.7101 3.02223 13.4439 4.3207 14.3898 5.99449L14.427 6.06021C15.373 7.73425 16.1068 9.03266 16.5641 10.0856C17.0236 11.1437 17.2525 12.0573 17.0699 12.935C16.886 13.8187 16.4327 14.6222 15.7724 15.2296C15.1137 15.8355 14.2168 16.0911 13.0875 16.2139C11.9647 16.3359 10.4958 16.3359 8.60532 16.3359H8.60526H8.52756H8.5275C6.637 16.3359 5.16815 16.3359 4.04527 16.2139C2.91598 16.0911 2.01911 15.8355 1.36044 15.2296C0.700153 14.6223 0.246776 13.8187 0.0629156 12.935C-0.119692 12.0573 0.109172 11.1437 0.568694 10.0856C1.02599 9.03266 1.75978 7.73424 2.70585 6.0602L2.74299 5.99449L2.74301 5.99446C3.68891 4.32069 4.42272 3.02222 5.08683 2.09096C5.75314 1.15659 6.41639 0.491571 7.26111 0.211173Z"
                fill="#CA7900"
              />
              <path
                d="M7.80078 7.37109C7.95992 7.37099 8.14951 7.37071 8.3125 7.39258C8.50936 7.41905 8.78161 7.4906 9.0127 7.72168C9.24378 7.95277 9.31533 8.22501 9.3418 8.42188C9.36367 8.58487 9.36338 8.77445 9.36328 8.93359V12.1523C9.36328 12.5924 9.00651 12.9492 8.56641 12.9492C8.1263 12.9492 7.76953 12.5924 7.76953 12.1523V8.96484C7.32943 8.96484 6.97266 8.60807 6.97266 8.16797C6.97267 7.72788 7.32944 7.37109 7.76953 7.37109C7.7798 7.37109 7.79023 7.3711 7.80078 7.37109ZM8.57031 4.18359C9.00829 4.18378 9.36328 4.54048 9.36328 4.98047C9.36328 5.42046 9.00829 5.77716 8.57031 5.77734H8.5625C8.12452 5.77716 7.76953 5.42046 7.76953 4.98047C7.76953 4.54048 8.12452 4.18378 8.5625 4.18359H8.57031Z"
                fill="#CA7900"
              />
            </svg>
          </IconCircle>
          <TextContent>
            <Title>{title}</Title>
            {descriptionHTML ? (
              <Description
                dangerouslySetInnerHTML={{ __html: descriptionHTML }}
              />
            ) : (
              <Description>{description}</Description>
            )}
          </TextContent>
          <CloseButton
            size="icon"
            variant="ghost"
            onClick={handleClose}
            aria-label="Close alert"
          >
            <CloseIcon />
          </CloseButton>
        </AlertContent>
        {footer && <Footer>{footer}</Footer>}
      </AlertFrame>
    </AlertContainer>
  );
};
